/**
 * Eva - AI Assistant Chat Interface
 * Natural language querying with saved views and visualizations
 */

import { useState, useRef, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Send, 
  Bot, 
  User, 
  Loader2, 
  MessageSquare, 
  Plus, 
  Trash2, 
  Archive, 
  Download,
  Table as TableIcon,
  BarChart3,
  PieChart,
  LineChart,
  Bookmark,
  BookmarkPlus,
  Clock,
  Sparkles,
  ChevronRight
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart as RechartsLine,
  Line,
  Legend
} from 'recharts';

// Chart colors
const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

interface Message {
  id: number;
  role: 'user' | 'eva' | 'system';
  content: string;
  createdAt: Date;
  generatedSql?: string | null;
  queryResults?: string | null;
  resultCount?: number | null;
  actionType?: string | null;
}

interface QueryResponse {
  message: string;
  data?: any[];
  columns?: string[];
  rowCount?: number;
  visualization?: 'table' | 'bar' | 'line' | 'pie' | 'summary';
  sql?: string;
  exportUrl?: string;
  processingTimeMs?: number;
}

export default function Eva() {
  const [message, setMessage] = useState("");
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [queryResponse, setQueryResponse] = useState<QueryResponse | null>(null);
  const [activeVisualization, setActiveVisualization] = useState<'table' | 'bar' | 'line' | 'pie'>('table');
  const [saveViewDialogOpen, setSaveViewDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Queries
  const { data: conversations, refetch: refetchConversations } = trpc.eva.getConversations.useQuery();
  const { data: messages, refetch: refetchMessages } = trpc.eva.getMessages.useQuery(
    { conversationId: currentConversationId! },
    { enabled: !!currentConversationId }
  );
  const { data: savedViews, refetch: refetchViews } = trpc.eva.getSavedViews.useQuery();
  const { data: mostUsedViews } = trpc.eva.getMostUsedViews.useQuery({ limit: 5 });

  // Mutations
  const chatMutation = trpc.eva.chat.useMutation({
    onSuccess: (result) => {
      setCurrentConversationId(result.conversationId);
      setQueryResponse(result.response);
      refetchMessages();
      refetchConversations();
    },
  });

  const createConversationMutation = trpc.eva.createConversation.useMutation({
    onSuccess: (conversation) => {
      if (conversation) {
        setCurrentConversationId(conversation.id);
        setQueryResponse(null);
        refetchConversations();
      }
    },
  });

  const deleteConversationMutation = trpc.eva.deleteConversation.useMutation({
    onSuccess: () => {
      if (conversations && conversations.length > 1) {
        setCurrentConversationId(conversations[0].id);
      } else {
        setCurrentConversationId(null);
      }
      refetchConversations();
    },
  });

  const createSavedViewMutation = trpc.eva.createSavedView.useMutation({
    onSuccess: () => {
      setSaveViewDialogOpen(false);
      setNewViewName("");
      refetchViews();
    },
  });

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || chatMutation.isPending) return;

    chatMutation.mutate({
      message: message.trim(),
      conversationId: currentConversationId,
    });
    setMessage("");
  };

  const handleNewConversation = () => {
    createConversationMutation.mutate({});
  };

  const handleDeleteConversation = (id: number) => {
    deleteConversationMutation.mutate({ conversationId: id });
  };

  const handleSaveView = () => {
    if (!newViewName.trim() || !queryResponse?.data) return;

    createSavedViewMutation.mutate({
      name: newViewName,
      queryType: 'custom',
      filters: {},
      defaultView: activeVisualization === 'table' ? 'table' : 'chart',
      chartType: activeVisualization !== 'table' ? activeVisualization : undefined,
    });
  };

  const handleExportCSV = () => {
    if (!queryResponse?.data || queryResponse.data.length === 0) return;

    const headers = Object.keys(queryResponse.data[0]);
    const csvContent = [
      headers.join(','),
      ...queryResponse.data.map(row => 
        headers.map(h => {
          const val = row[h];
          if (typeof val === 'string' && val.includes(',')) {
            return `"${val}"`;
          }
          return val ?? '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `eva-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Render data table
  const renderTable = (data: any[]) => {
    if (!data || data.length === 0) return null;
    const columns = Object.keys(data[0]);

    return (
      <div className="rounded-md border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col} className="font-semibold">
                  {col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.slice(0, 100).map((row, i) => (
              <TableRow key={i}>
                {columns.map((col) => (
                  <TableCell key={col}>
                    {typeof row[col] === 'number' 
                      ? row[col].toLocaleString() 
                      : row[col]?.toString() || '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data.length > 100 && (
          <div className="p-2 text-center text-sm text-muted-foreground bg-muted/50">
            Showing 100 of {data.length} rows
          </div>
        )}
      </div>
    );
  };

  // Render bar chart
  const renderBarChart = (data: any[]) => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0]);
    const labelKey = keys[0];
    const valueKeys = keys.slice(1).filter(k => typeof data[0][k] === 'number');

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={data.slice(0, 20)}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey={labelKey} stroke="#9ca3af" fontSize={12} />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip 
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
            labelStyle={{ color: '#f9fafb' }}
          />
          <Legend />
          {valueKeys.map((key, i) => (
            <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Render line chart
  const renderLineChart = (data: any[]) => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0]);
    const labelKey = keys[0];
    const valueKeys = keys.slice(1).filter(k => typeof data[0][k] === 'number');

    return (
      <ResponsiveContainer width="100%" height={400}>
        <RechartsLine data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey={labelKey} stroke="#9ca3af" fontSize={12} />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip 
            contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
            labelStyle={{ color: '#f9fafb' }}
          />
          <Legend />
          {valueKeys.map((key, i) => (
            <Line 
              key={key} 
              type="monotone" 
              dataKey={key} 
              stroke={CHART_COLORS[i % CHART_COLORS.length]} 
              strokeWidth={2}
            />
          ))}
        </RechartsLine>
      </ResponsiveContainer>
    );
  };

  // Render pie chart
  const renderPieChart = (data: any[]) => {
    if (!data || data.length === 0) return null;
    const keys = Object.keys(data[0]);
    const labelKey = keys[0];
    const valueKey = keys.find(k => typeof data[0][k] === 'number') || keys[1];

    const pieData = data.slice(0, 10).map((item, i) => ({
      name: item[labelKey],
      value: item[valueKey],
      fill: CHART_COLORS[i % CHART_COLORS.length],
    }));

    return (
      <ResponsiveContainer width="100%" height={400}>
        <RechartsPie>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
            outerRadius={150}
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          <Tooltip />
        </RechartsPie>
      </ResponsiveContainer>
    );
  };

  // Quick prompts for new users
  const quickPrompts = [
    "Show me all active accounts",
    "Total balance by placement?",
    "Payments collected this month?",
    "Accounts with balance over $5000",
    "Collection rate by status?",
    "Show recent payment activity",
  ];

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar - Conversations & Saved Views */}
        <div className="w-72 border-r border-border flex flex-col">
          <div className="p-4 border-b border-border">
            <Button onClick={handleNewConversation} className="w-full" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              New Conversation
            </Button>
          </div>

          <Tabs defaultValue="conversations" className="flex-1 flex flex-col">
            <TabsList className="mx-4 mt-2">
              <TabsTrigger value="conversations" className="flex-1">
                <MessageSquare className="h-4 w-4 mr-1" />
                Chats
              </TabsTrigger>
              <TabsTrigger value="views" className="flex-1">
                <Bookmark className="h-4 w-4 mr-1" />
                Views
              </TabsTrigger>
            </TabsList>

            <TabsContent value="conversations" className="flex-1 m-0">
              <ScrollArea className="h-full">
                <div className="p-2 space-y-1">
                  {conversations?.map((conv) => (
                    <div
                      key={conv.id}
                      className={`group flex items-center justify-between p-2 rounded-md cursor-pointer hover:bg-muted ${
                        currentConversationId === conv.id ? 'bg-muted' : ''
                      }`}
                      onClick={() => {
                        setCurrentConversationId(conv.id);
                        setQueryResponse(null);
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{conv.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(conv.updatedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConversation(conv.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {(!conversations || conversations.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No conversations yet
                    </p>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="views" className="flex-1 m-0">
              <ScrollArea className="h-full">
                <div className="p-2 space-y-1">
                  {mostUsedViews && mostUsedViews.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-muted-foreground px-2 mb-2">
                        FREQUENTLY USED
                      </p>
                      {mostUsedViews.map((view) => (
                        <div
                          key={view.id}
                          className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted"
                        >
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm truncate">{view.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Separator />
                  <p className="text-xs font-medium text-muted-foreground px-2 my-2">
                    ALL SAVED VIEWS
                  </p>
                  {savedViews?.map((view) => (
                    <div
                      key={view.id}
                      className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted"
                    >
                      <Bookmark className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm truncate">{view.name}</span>
                    </div>
                  ))}
                  {(!savedViews || savedViews.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No saved views yet
                    </p>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="max-w-4xl mx-auto space-y-4">
              {/* Welcome message for new conversations */}
              {(!messages || messages.length === 0) && !chatMutation.isPending && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Hi, I'm Eva</h2>
                  <p className="text-muted-foreground mb-6">
                    Your AI assistant for querying data, generating reports, and managing your workflow.
                  </p>
                  <div className="grid grid-cols-2 gap-2 max-w-2xl mx-auto">
                    {quickPrompts.map((prompt, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        className="justify-start text-left h-auto py-2 px-3 whitespace-normal"
                        onClick={() => {
                          setMessage(prompt);
                        }}
                      >
                        <ChevronRight className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="text-sm leading-snug">{prompt}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Message history */}
              {messages?.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                >
                  {msg.role !== 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    {msg.resultCount !== null && msg.resultCount !== undefined && (
                      <Badge variant="secondary" className="mt-2">
                        {msg.resultCount} results
                      </Badge>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                      <User className="h-4 w-4 text-primary-foreground" />
                    </div>
                  )}
                </div>
              ))}

              {/* Loading indicator */}
              {chatMutation.isPending && (
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="bg-muted rounded-lg p-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Results Visualization */}
          {queryResponse?.data && queryResponse.data.length > 0 && (
            <div className="border-t border-border p-4 bg-muted/30">
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{queryResponse.rowCount} results</Badge>
                    {queryResponse.processingTimeMs && (
                      <span className="text-xs text-muted-foreground">
                        {queryResponse.processingTimeMs}ms
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Visualization toggle */}
                    <div className="flex items-center border rounded-md">
                      <Button
                        variant={activeVisualization === 'table' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveVisualization('table')}
                      >
                        <TableIcon className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={activeVisualization === 'bar' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveVisualization('bar')}
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={activeVisualization === 'line' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveVisualization('line')}
                      >
                        <LineChart className="h-4 w-4" />
                      </Button>
                      <Button
                        variant={activeVisualization === 'pie' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveVisualization('pie')}
                      >
                        <PieChart className="h-4 w-4" />
                      </Button>
                    </div>
                    <Separator orientation="vertical" className="h-6" />
                    {/* Actions */}
                    <Dialog open={saveViewDialogOpen} onOpenChange={setSaveViewDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm">
                          <BookmarkPlus className="h-4 w-4 mr-1" />
                          Save View
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Save View</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>View Name</Label>
                            <Input
                              value={newViewName}
                              onChange={(e) => setNewViewName(e.target.value)}
                              placeholder="e.g., Active Accounts This Month"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setSaveViewDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={handleSaveView} disabled={!newViewName.trim()}>
                            Save View
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Button variant="outline" size="sm" onClick={handleExportCSV}>
                      <Download className="h-4 w-4 mr-1" />
                      Export CSV
                    </Button>
                  </div>
                </div>

                {/* Visualization content */}
                <Card>
                  <CardContent className="p-4">
                    {activeVisualization === 'table' && renderTable(queryResponse.data)}
                    {activeVisualization === 'bar' && renderBarChart(queryResponse.data)}
                    {activeVisualization === 'line' && renderLineChart(queryResponse.data)}
                    {activeVisualization === 'pie' && renderPieChart(queryResponse.data)}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-border p-4">
            <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-2">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask Eva anything... e.g., 'Show me all accounts with balance over $5000'"
                className="flex-1"
                disabled={chatMutation.isPending}
              />
              <Button type="submit" disabled={!message.trim() || chatMutation.isPending}>
                {chatMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
